/**
 * WebEngageService - WebEngage analytics integration
 */

import { WebEngageInstance } from '../../types/analytics.types';
import { createLogger } from '../Logger';

const log = createLogger('WebEngageService');

declare global {
  interface Window {
    webengage?: WebEngageInstance;
  }
}

class WebEngageService {
  private initialized = false;

  /**
   * Check if WebEngage is available and initialize
   */
  private ensureInitialized(): boolean {
    if (typeof window === 'undefined') return false;
    
    if (window.webengage) {
      this.initialized = true;
      return true;
    }
    
    return false;
  }

  /**
   * Login user to WebEngage
   * @param userId - Customer ID
   */
  login(userId: string): void {
    if (!this.ensureInitialized()) return;
    
    try {
      window.webengage?.user.login(userId);
    } catch (error) {
      log.warn('Login error', { error });
    }
  }

  /**
   * Logout user from WebEngage
   */
  logout(): void {
    if (!this.ensureInitialized()) return;
    
    try {
      window.webengage?.user.logout();
    } catch (error) {
      log.warn('Logout error', { error });
    }
  }

  /**
   * Set user attribute
   * @param key - Attribute key
   * @param value - Attribute value
   */
  setAttribute(key: string, value: string | number | boolean): void {
    if (!this.ensureInitialized()) return;
    
    try {
      window.webengage?.user.setAttribute(key, value);
    } catch (error) {
      log.warn('setAttribute error', { error });
    }
  }

  /**
   * Set multiple user attributes
   * @param attributes - Object containing attribute key-value pairs
   */
  setAttributes(attributes: Record<string, string | number | boolean>): void {
    if (!this.ensureInitialized()) return;
    
    try {
      Object.keys(attributes).forEach(key => {
        window.webengage?.user.setAttribute(key, attributes[key]);
      });
    } catch (error) {
      log.warn('setAttributes error', { error });
    }
  }

  /**
   * Track event
   * @param eventName - Event name
   * @param eventData - Optional event data
   */
  track(eventName: string, eventData?: Record<string, unknown>): void {
    if (!this.ensureInitialized()) return;
    
    try {
      window.webengage?.track(eventName, eventData);
    } catch (error) {
      log.warn('Track error', { error });
    }
  }

  /**
   * Set user email
   * @param email - User email
   */
  setEmail(email: string): void {
    this.setAttribute('we_email', email);
  }

  /**
   * Set user phone number
   * @param phone - User phone number
   */
  setPhone(phone: string): void {
    this.setAttribute('we_phone', phone);
  }

  /**
   * Set user first name
   * @param firstName - User first name
   */
  setFirstName(firstName: string): void {
    this.setAttribute('we_first_name', firstName);
  }

  /**
   * Set user last name
   * @param lastName - User last name
   */
  setLastName(lastName: string): void {
    this.setAttribute('we_last_name', lastName);
  }

  /**
   * Track merchant click event
   * @param merchantName - Name of the merchant
   * @param creditLimit - Available credit limit
   */
  trackMerchantClick(merchantName: string, creditLimit: number): void {
    this.track('Merchant Clicked', {
      merchantName,
      creditLimit
    });
  }

  /**
   * Track credit limit loaded event
   * @param amount - Credit limit amount
   * @param status - Credit limit status
   */
  trackCreditLimitLoaded(amount: number, status: string): void {
    this.track('Credit Limit Loaded', {
      amount,
      status
    });
  }

  /**
   * Track personal loan event
   * @param action - Action taken (applied, approved, etc.)
   * @param amount - Loan amount
   * @param tenure - Loan tenure
   */
  trackPersonalLoan(action: string, amount?: number, tenure?: number): void {
    this.track('Personal Loan', {
      action,
      ...(amount !== undefined && { amount }),
      ...(tenure !== undefined && { tenure })
    });
  }

  /**
   * Track payment event
   * @param eventName - Payment event name
   * @param data - Payment data
   */
  trackPayment(
    eventName: string,
    data: {
      loanId: string;
      amount: number;
      paymentType: string;
      status?: string;
    }
  ): void {
    this.track(eventName, data);
  }

  /**
   * Track download app event
   * @param source - Source of download (banner, etc.)
   */
  trackDownloadApp(source: string): void {
    this.track('Download App Clicked', { source });
  }

  /**
   * Track page view
   * @param pageName - Name of the page
   */
  trackPageView(pageName: string): void {
    this.track('Page Viewed', { pageName });
  }

  /**
   * Execute callback when WebEngage is ready
   * @param callback - Callback to execute
   */
  onReady(callback: () => void): void {
    if (this.ensureInitialized()) {
      try {
        window.webengage?.onReady(callback);
      } catch {
        callback();
      }
    }
  }
}

// Export singleton instance
const webEngageService = new WebEngageService();
export default webEngageService;

// Also export the class for testing
export { WebEngageService };
